import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import * as IssuesV2Store from '../../../../redux/issuesV2';
import { LICENSING_ISSUE_CONFIG } from '../../../../shared/issuesV2/issueConfig';
import { Badge } from '../../../generic/Badge';
import PackageLocator from '../../../shared/issues/PackageLocator';
import type { LicensingIssue } from '../../../../../shared/issuesV2';

import styles from '../IssueContent.module.less';

/**
 * Some package managers have numeric identifiers, or include
 * additional org identifier, which are not representative of
 * package title. Provide preferred package title, if any.
 */
const preferredPackageTitleOf = (issue: LicensingIssue | null) => {
  const pkgKinds = ['path', 'archive', 'user'];
  if (issue && pkgKinds.includes(issue.source.packageManager)) {
    return issue.source.name;
  }

  return undefined;
};

/**
 * LicensingIssueDetailsHeader displays a badge with the issue type,
 * as well as the license + PackageLocator where the issue was found.
 */
const LicensingIssueDetailsHeader: VoidFunctionComponent = () => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails) as LicensingIssue;
  const locatorParts = useSelector(IssuesV2Store.selectors.getIssueDetailsSourceLocatorParts);

  if (!issueDetails) {
    return null;
  }

  return (
    <header className={styles.header}>
      <div className={styles.headerBadge}>
        <Badge
          color={LICENSING_ISSUE_CONFIG[issueDetails.type]?.color}
          icon={LICENSING_ISSUE_CONFIG[issueDetails.type]?.icon}
          fullWidth
        >
          {LICENSING_ISSUE_CONFIG[issueDetails.type]?.label}
        </Badge>
      </div>
      <div className={styles.heading}>
        {issueDetails.license || 'No license found'}
      </div>
      {locatorParts && (
        <PackageLocator
          locatorParts={locatorParts}
          isPrefixDisplayed
          preferredPackageTitle={preferredPackageTitleOf(issueDetails)}
        />
      )}
    </header>
  );
};

export default LicensingIssueDetailsHeader;
